import { supabase } from '../lib/supabase';
import { supabaseAdmin } from '../lib/supabaseAdmin';
import type { Session } from '@supabase/supabase-js';

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'distribuidor' | 'reseller';
  status: 'active' | 'pending' | 'blocked';
  platforms_enabled: string[];   // e.g. ['2onamedia', 'onetv']
  xui_username: string;          // usuario del reseller en el panel XUI
}

export interface AuthResponse {
  session: Session | null;
  user: User;
}

export const authService = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) throw new Error('Correo o contraseña incorrectos');

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role, status, full_name')
      .eq('id', authData.user.id)
      .single();

    if (profileError || !profile) throw new Error('No se encontró el perfil del usuario');

    if (profile.status === 'pending') {
      await supabase.auth.signOut();
      throw new Error('Tu cuenta está pendiente de activación. Contacta al administrador.');
    }
    if (profile.status === 'blocked') {
      await supabase.auth.signOut();
      throw new Error('Tu cuenta ha sido bloqueada. Contacta al administrador.');
    }

    // Para resellers, cargar plataformas habilitadas y username XUI
    let platforms_enabled: string[] = ['2onamedia'];
    let xui_username = '';
    if (profile.role === 'reseller') {
      const { data: resellerData } = await supabase
        .from('resellers')
        .select('platforms_enabled, xui_username')
        .eq('id', authData.user.id)
        .single();
      if (resellerData) {
        platforms_enabled = resellerData.platforms_enabled || ['2onamedia'];
        xui_username = resellerData.xui_username || '';
      }
    }

    return {
      session: authData.session,
      user: {
        id: authData.user.id,
        email: authData.user.email || '',
        role: profile.role,
        status: profile.status,
        full_name: profile.full_name,
        platforms_enabled,
        xui_username,
      },
    };
  },

  // Construye el User desde un userId/email sin llamar getSession() — seguro dentro de onAuthStateChange
  async getUserFromSession(userId: string, email: string): Promise<User | null> {
    const { data: profile } = await supabase
      .from('profiles')
      .select('role, status, full_name')
      .eq('id', userId)
      .single();

    if (!profile) return null;

    let platforms_enabled: string[] = ['2onamedia'];
    let xui_username = '';
    if (profile.role === 'reseller') {
      const { data: resellerData } = await supabase
        .from('resellers')
        .select('platforms_enabled, xui_username')
        .eq('id', userId)
        .single();
      if (resellerData) {
        platforms_enabled = resellerData.platforms_enabled || ['2onamedia'];
        xui_username = resellerData.xui_username || '';
      }
    }

    return {
      id: userId,
      email,
      role: profile.role,
      status: profile.status,
      full_name: profile.full_name,
      platforms_enabled,
      xui_username,
    };
  },

  async getCurrentUser(): Promise<User | null> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    return authService.getUserFromSession(session.user.id, session.user.email || '');
  },

  async logout(): Promise<void> {
    await supabase.auth.signOut();
  },

  async loginWithGoogle(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/login',
      },
    });
    if (error) throw new Error('Error al iniciar sesión con Google: ' + error.message);
  },

  async register(email: string, password: string, fullName: string): Promise<void> {
    const { data, error } = await supabaseAdmin.auth.admin.createUser({
      email,
      password,
      email_confirm: true,
      user_metadata: { full_name: fullName },
    });

    if (error) throw new Error(error?.message || 'Error al crear el usuario');
    if (!data?.user) throw new Error('No se pudo crear el usuario');
    // El trigger handle_new_user crea el perfil automáticamente con status=pending
  },

  async updateUserPassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error('Error al actualizar contraseña: ' + error.message);
  },
};

export default authService;
